/**
 * Aufgaben — EINE Liste mit Kontext (Konzept ~/maestro-uebergabe/konzept-aufgaben.html, Stufe 1+2;
 * Antoine 26.09.2026: „Hier muss stehen, der Event, Datum, Kunde etc. So ist es nutzlos.“).
 *
 * Jede Zeile beantwortet ohne Klick: welcher Event (Anlass, Datum + Tage bis dahin), für wen
 * (Firma/Kunde, Gäste, Vorgangsstatus), was ist zu tun und warum (Grund-Satz vom Server, Betrag
 * nur aus der DB) — und genau EINE Aktion springt in den richtigen Schritt des Vorgangs.
 *
 * Entscheidungen (alle nach Empfehlung): E1 „Antwort abwarten“ ist keine Aufgabe, sondern steht
 * eingeklappt unter „Kunde ist dran“; E2 gruppiert Überfällig · Heute · Diese Woche · Später,
 * innen nach Event-Datum, Umschalter „nach Event-Datum“; E3 Kanban entfällt, Notizen bekommen
 * ein Häkchen (der Status bleibt in der Datenbank); E5 der Grund-Satz entsteht serverseitig.
 *
 * Stufe 3: Zeile als gemeinsame Komponente (`ui/AufgabenZeile.tsx`, auch im Dashboard),
 * Zuständige mit Namen + Filter „Nur meine“, „Was ist diese Woche passiert“ (E4 = A, abgeleitet
 * aus vorhandenen Zeitstempeln, Satz vom Server).
 *
 * Datenquelle unverändert GET /api/tasks/board (dieselbe wie Dashboard und Assistent).
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { anlassAnzeige } from "../ui/format";
import { Link, useNavigate } from "react-router-dom";
import { GRUPPEN_TITEL } from "@maestro/anfrage-stand";
import { useApi, apiMutate } from "../hooks/useApi";
import { useContentRefetch } from "../ui/CopilotProvider";
import { uebersetzeAltesZiel, vorgangPfad } from "../lib/pfade";
import {
  ART_TITEL,
  heuteBerlin,
  gruppeVon,
  istMeine,
  mitgliedName,
  nachEventDatum,
  ordneAufgaben,
  zeileAusAuto,
  zeileAusNotiz,
  type AufgabenArt,
  type AufgabenZeile,
} from "../lib/aufgaben-liste";
import type { ErledigtEintrag, EventRow, Me, Member, TasksBoard } from "../ui/types";
import { AufgabenZeileAnsicht, eventTag } from "../ui/AufgabenZeile";
import { Button, Card, ChipSelect, Drawer, EmptyState, ErrorNote, Icon, Input, ListenReiter, LoadingRows, PageHeader, Select, SortierMenue, DateInput, Textarea } from "../ui/components";
import { useListSort, type SortSpalte } from "../hooks/useListSort";

// Design-System-Migration P2 (`tasks.tsx`, analog `catalog.tsx`/`settings.tsx` Kopfkommentar
// `KEIN_WERT`): Sentinel fuer die beiden Anlegen-Drawer-Selects mit einer echten, waehlbaren
// leeren Option ("— keiner —"/"— niemand —") — Radix' `Select.Item` nimmt keinen leeren
// `value`-String an.
const KEIN_WERT = "__kein_wert__";

/** E2-Umschalter über das Listen-Sortiermenü (URL `?sort=event`, wie jede andere Liste). */
const AUFGABEN_SPALTEN: SortSpalte<AufgabenZeile>[] = [{ key: "event", label: "Event-Datum", type: "date", accessor: (z) => z.eventDate }];
const AUFGABEN_SORTIERUNG = [
  { label: "Nach Dringlichkeit", key: null },
  { label: "Nach Event-Datum", key: "event", dir: "asc" },
] as const;

type Reiter = "alle" | AufgabenArt;
const REITER_ARTEN: readonly AufgabenArt[] = ["zahlung", "angebot", "anfrage", "notiz", "betrieb"];

/** „Heute“ · „Gestern“ · „Do“ — Tag eines Erledigt-Eintrags (Berliner Kalendertag). */
function erledigtTag(amIso: string, heute: string): string {
  const tag = heuteBerlin(new Date(amIso));
  const t = Math.round((Date.parse(`${heute}T00:00:00Z`) - Date.parse(`${tag}T00:00:00Z`)) / 86_400_000);
  if (t <= 0) return "Heute";
  if (t === 1) return "Gestern";
  return new Date(`${tag}T12:00:00Z`).toLocaleDateString("de-DE", { weekday: "short", timeZone: "Europe/Berlin" }).replace(".", "");
}

const ERLEDIGT_ICON: Record<ErledigtEintrag["art"], string> = {
  zahlung: "payments",
  zusage: "event_available",
  angenommen: "handshake",
  notiz: "check_circle",
  weggeklickt: "do_not_disturb_on",
};

/**
 * Stufe 3 (E4 = A): „Was ist diese Woche passiert“ — abgeleitet aus vorhandenen Zeitstempeln,
 * der Satz kommt fertig vom Server. Eingeklappt wie „Kunde ist dran“: nur zur Info.
 */
function ErledigtVerlauf({ eintraege, heute }: { eintraege: ErledigtEintrag[]; heute: string }) {
  if (eintraege.length === 0) return null;
  return (
    <Card className="mb-5 overflow-hidden">
      <details data-testid="erledigt-verlauf">
        <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3.5 text-[13.5px] text-on-background dark:text-inverse-on-surface md:px-[18px]">
          <Icon name="task_alt" className="text-[18px] text-[#6F675A] dark:text-[#b8ae9e]" />
          <span className="font-semibold">Was ist diese Woche passiert</span>
          <span className="text-[#6F675A] dark:text-secondary-fixed-dim">· {eintraege.length} erledigt in den letzten 7 Tagen</span>
          <Icon name="expand_more" className="ml-auto text-[18px] text-[#9A9080]" />
        </summary>
        <ul className="border-t border-[#F2ECDF] dark:border-white/[0.06]">
          {eintraege.map((e) => {
            const inhalt = (
              <>
                <Icon name={ERLEDIGT_ICON[e.art]} className="mt-px shrink-0 text-[16px] text-[#6F675A] dark:text-[#b8ae9e]" />
                <span className="w-[62px] shrink-0 text-[12px] font-medium text-[#9A9080] dark:text-secondary-fixed-dim">{erledigtTag(e.am, heute)}</span>
                <span className="min-w-0 flex-1 text-on-background dark:text-inverse-on-surface">{e.satz}</span>
              </>
            );
            const klasse = "flex items-start gap-2.5 px-4 py-2.5 text-[13px] md:px-[18px]";
            return (
              <li key={e.key} data-testid="erledigt-eintrag" className="border-b border-[#F6F0E4] last:border-b-0 dark:border-white/[0.05]">
                {e.ziel ? (
                  <Link to={uebersetzeAltesZiel(e.ziel)} className={`${klasse} hover:bg-[#FBF6EC] dark:hover:bg-white/[0.03]`}>
                    {inhalt}
                  </Link>
                ) : (
                  <div className={klasse}>{inhalt}</div>
                )}
              </li>
            );
          })}
        </ul>
      </details>
    </Card>
  );
}

export default function TasksPage() {
  const boardQ = useApi<TasksBoard>("/api/tasks/board");
  // Chat-P1: create_task/update_task per Chat -> Board live nachladen.
  useContentRefetch("task", () => boardQ.reload());
  const eventsQ = useApi<EventRow[]>("/api/events");
  const membersQ = useApi<Member[]>("/api/members");
  // Stufe 3: „Nur meine“ braucht die eigene Stack-User-ID (dieselbe Quelle wie MitgliedschaftsGate).
  const meQ = useApi<Me>("/api/me");
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Design-System-Migration P2 (s. Kopfkommentar `KEIN_WERT`): Prioritaet/Vorgang/Zuestaendig
  // kommen seit dem Umstieg der drei Selects auf `<Select>` (Radix) aus lokalem React-State statt
  // aus `new FormData(e.currentTarget)` — Radix' Select erzeugt kein natives, FormData-taugliches
  // Element. Titel/Faelligkeitsdatum/Notizen bleiben unveraendert per FormData ausgelesen (echte
  // `<input>`/`<textarea>`-Elemente).
  const [formPriority, setFormPriority] = useState("normal");
  const [formEventId, setFormEventId] = useState(KEIN_WERT);
  const [formAssignee, setFormAssignee] = useState(KEIN_WERT);
  // Bei jedem Oeffnen zuruecksetzen - sonst zeigt ein zweiter Aufruf noch die Auswahl des
  // vorherigen (gleiches Muster wie `catDrawerOpen` in `catalog.tsx`).
  useEffect(() => {
    if (createOpen) {
      setFormPriority("normal");
      setFormEventId(KEIN_WERT);
      setFormAssignee(KEIN_WERT);
    }
  }, [createOpen]);
  // Optimistisch: Wegklicken (Auto) und Abhaken/Löschen (Notiz) blenden sofort aus; schlägt der
  // Server-Aufruf fehl, kommt die Zeile zurück (kein "verschwindet fuer immer" bei einem Netzfehler).
  const [ausgeblendet, setAusgeblendet] = useState<Set<string>>(new Set());
  const blende = (key: string, an: boolean) =>
    setAusgeblendet((prev) => {
      const next = new Set(prev);
      if (an) next.add(key);
      else next.delete(key);
      return next;
    });
  const [reiter, setReiter] = useState<Reiter>("alle");
  const [wer, setWer] = useState<"alle" | "meine">("alle");
  const sort = useListSort("aufgaben", AUFGABEN_SPALTEN);
  const modus = sort.sortKey === "event" ? "event" : "dringlichkeit";
  const heute = heuteBerlin();

  const events = eventsQ.data ?? [];
  const members = membersQ.data ?? [];

  const { aufgaben, wartend } = useMemo(() => {
    const auto = (boardQ.data?.auto ?? []).filter((a) => !ausgeblendet.has(a.signature));
    const notizen = (boardQ.data?.manual ?? []).filter((t) => t.status !== "done" && !ausgeblendet.has(`notiz:${t.id}`));
    return {
      aufgaben: [...auto.filter((a) => !a.kundeIstDran).map(zeileAusAuto), ...notizen.map((t) => zeileAusNotiz(t, mitgliedName(membersQ.data ?? [], t.assignee)))],
      wartend: auto.filter((a) => a.kundeIstDran).map(zeileAusAuto).sort(nachEventDatum),
    };
  }, [boardQ.data, membersQ.data, ausgeblendet]);
  const ichId = meQ.data?.userId ?? null;
  // Reiter-Zahlen folgen dem Zuständig-Filter (sonst stünde bei „Nur meine“ weiter „Alle 10“).
  const basis = wer === "alle" ? aufgaben : aufgaben.filter((z) => istMeine(z, ichId));
  const sichtbar = reiter === "alle" ? basis : basis.filter((z) => z.art === reiter);
  const abschnitte = ordneAufgaben(sichtbar, heute, modus);
  const ueberfaellig = aufgaben.filter((z) => gruppeVon(z.faelligAm, heute) === "ueberfaellig").length;
  const reiterListe = [
    { key: "alle" as Reiter, label: "Alle", anzahl: basis.length },
    ...REITER_ARTEN.map((a) => ({ key: a as Reiter, label: ART_TITEL[a], anzahl: basis.filter((z) => z.art === a).length })).filter(
      (r) => r.anzahl > 0 || r.key === reiter,
    ),
  ];

  async function abhaken(id: string) {
    blende(`notiz:${id}`, true);
    try {
      await apiMutate(`/api/tasks/${id}`, "PATCH", { status: "done" });
    } catch (e) {
      blende(`notiz:${id}`, false);
      setFormError(e instanceof Error ? e.message : String(e));
    }
  }
  async function remove(id: string) {
    blende(`notiz:${id}`, true);
    try {
      await apiMutate(`/api/tasks/${id}`, "DELETE");
    } catch (e) {
      blende(`notiz:${id}`, false);
      setFormError(e instanceof Error ? e.message : String(e));
    }
  }

  /** Auto-Task wegklicken: sofort optimistisch ausblenden, bei Fehler zurueckrollen. */
  async function dismissAuto(signature: string) {
    blende(signature, true);
    try {
      await apiMutate("/api/tasks/board/dismiss", "POST", { signature });
    } catch (e) {
      blende(signature, false);
      setFormError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    setFormError(null);
    try {
      await apiMutate("/api/tasks", "POST", {
        title: form.get("title"),
        eventId: formEventId === KEIN_WERT ? null : formEventId,
        assignee: formAssignee === KEIN_WERT ? null : formAssignee,
        dueDate: (form.get("dueDate") as string) || null,
        priority: formPriority,
        notes: (form.get("notes") as string) || null,
      });
      setCreateOpen(false);
      boardQ.reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const neu = () => {
    setFormError(null);
    setCreateOpen(true);
  };

  return (
    <>
      <PageHeader
        title="Aufgaben"
        subtitle={
          boardQ.data
            ? `Alles, wo du dran bist. ${aufgaben.length} offen${ueberfaellig > 0 ? `, davon ${ueberfaellig} überfällig` : ""}.`
            : "Alles, wo du dran bist."
        }
        actions={
          <Button type="button" onClick={neu}>
            <Icon name="add" className="text-[18px]" /> Aufgabe
          </Button>
        }
      />

      {formError && <ErrorNote message={formError} />}

      {boardQ.loading ? (
        <Card className="overflow-hidden">
          <LoadingRows rows={6} />
        </Card>
      ) : boardQ.error ? (
        <Card className="overflow-hidden">
          <ErrorNote zentriert title="Aufgaben konnten nicht geladen werden" message={boardQ.error} onRetry={boardQ.reload} />
        </Card>
      ) : aufgaben.length === 0 && wartend.length === 0 ? (
        <Card>
          <EmptyState icon="checklist" title="Noch keine Aufgaben" hint="Legen Sie die erste Aufgabe an." cta={{ label: "Aufgabe anlegen", icon: "add", onClick: neu }} />
        </Card>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
            <ChipSelect
              testId="filter-zustaendig"
              wert={wer}
              onWechsel={(w) => setWer(w === "meine" ? "meine" : "alle")}
              icon="person"
              ariaLabel="Zuständig"
              neutral="alle"
              optionen={[
                { wert: "alle", text: "Alle Zuständigen" },
                { wert: "meine", text: "Nur meine" },
              ]}
            />
            <SortierMenue optionen={AUFGABEN_SORTIERUNG} sortKey={sort.sortKey} dir={sort.dir} onWahl={sort.setze} />
          </div>
          <Card className="mb-5 overflow-hidden">
            <ListenReiter label="Aufgaben nach Art" reiter={reiterListe} aktiv={reiter} onWahl={setReiter} />
            {abschnitte.length === 0 ? (
              <p className="px-[18px] py-6 text-center text-[13px] text-[#6F675A] dark:text-secondary-fixed-dim">
                {aufgaben.length === 0
                  ? "Nichts zu tun — alles liegt gerade beim Kunden."
                  : wer === "meine"
                    ? "Dir ist gerade nichts zugewiesen."
                    : "In dieser Auswahl ist nichts offen."}
              </p>
            ) : (
              abschnitte.map((a) => (
                <section key={a.key} aria-label={a.titel}>
                  <h2
                    className={`flex items-center gap-2 border-b border-[#F2ECDF] bg-[#FBF7EF] px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.04em] dark:border-white/[0.06] dark:bg-white/[0.03] md:px-[18px] ${
                      a.key === "ueberfaellig" ? "text-error" : "text-[#6F675A] dark:text-secondary-fixed-dim"
                    }`}
                  >
                    {a.titel}
                    <span className="rounded-full bg-[#F4EEE1] px-[7px] py-0.5 text-[10.5px] text-[#6F675A] dark:bg-white/[0.08] dark:text-secondary-fixed-dim">{a.zeilen.length}</span>
                  </h2>
                  <ul>
                    {a.zeilen.map((z) => (
                      <AufgabenZeileAnsicht
                        key={z.key}
                        z={z}
                        heute={heute}
                        onAktion={(ziel) => navigate(uebersetzeAltesZiel(ziel))}
                        onWegklicken={(sig) => void dismissAuto(sig)}
                        onAbhaken={(id) => void abhaken(id)}
                        onLoeschen={(id) => void remove(id)}
                      />
                    ))}
                  </ul>
                </section>
              ))
            )}
          </Card>

          {/* E1: „Antwort abwarten“ ist keine Aufgabe — nur zur Info, eingeklappt. */}
          {wartend.length > 0 && wer === "alle" && (reiter === "alle" || reiter === "angebot") && (
            <Card className="mb-5 overflow-hidden">
              <details>
                <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3.5 text-[13.5px] text-on-background dark:text-inverse-on-surface md:px-[18px]">
                  <Icon name="hourglass_top" className="text-[18px] text-[#9A9080]" />
                  <span className="font-semibold">{GRUPPEN_TITEL.kunde_ist_dran}</span>
                  <span className="text-[#6F675A] dark:text-secondary-fixed-dim">
                    · {wartend.length} {wartend.length === 1 ? "Angebot wartet" : "Angebote warten"} auf Antwort (keine Aufgabe, nur zur Info)
                  </span>
                  <Icon name="expand_more" className="ml-auto text-[18px] text-[#9A9080]" />
                </summary>
                <ul className="border-t border-[#F2ECDF] dark:border-white/[0.06]">
                  {wartend.map((z) => (
                    <li key={z.key} className="border-b border-[#F6F0E4] last:border-b-0 dark:border-white/[0.05]">
                      <Link
                        to={z.auto?.eventId ? vorgangPfad(z.auto.eventId) : uebersetzeAltesZiel(z.aktion?.ziel ?? "/")}
                        className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 px-4 py-2.5 text-[13px] hover:bg-[#FBF6EC] dark:hover:bg-white/[0.03] md:px-[18px]"
                      >
                        <span className="font-medium text-on-background dark:text-inverse-on-surface">
                          {[z.anlass, z.kunde].filter(Boolean).join(" · ")}
                        </span>
                        <span className="text-[#6F675A] dark:text-secondary-fixed-dim">
                          {[z.gaeste != null ? `${z.gaeste} Gäste` : null, z.eventDate ? eventTag(z.eventDate) : null, z.kopf].filter(Boolean).join(" · ")}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </details>
            </Card>
          )}
        </>
      )}

      {boardQ.data && <ErledigtVerlauf eintraege={boardQ.data.erledigt ?? []} heute={heute} />}

      {/* Anlegen */}
      <Drawer open={createOpen} onClose={() => setCreateOpen(false)} title="Aufgabe anlegen">
        <form className="space-y-4" onSubmit={(e) => void handleCreate(e)}>
          {formError && <ErrorNote message={formError} />}
          <Field label="Titel *"><Input name="title" required /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Priorität">
              <Select
                value={formPriority}
                onValueChange={setFormPriority}
                options={[
                  { value: "low", label: "Niedrig" },
                  { value: "normal", label: "Normal" },
                  { value: "high", label: "Hoch" },
                ]}
              />
            </Field>
            <Field label="Fällig am"><DateInput name="dueDate" /></Field>
          </div>
          <Field label="Vorgang (optional)">
            <Select
              value={formEventId}
              onValueChange={setFormEventId}
              options={[
                { value: KEIN_WERT, label: "— keiner —" },
                ...events.map((e) => ({ value: e.id, label: [anlassAnzeige(e.occasion) ?? "Vorgang", e.number].filter(Boolean).join(" · ") })),
              ]}
            />
          </Field>
          <Field label="Zuständig (optional)">
            <Select
              value={formAssignee}
              onValueChange={setFormAssignee}
              options={[
                { value: KEIN_WERT, label: "— niemand —" },
                ...members.map((m) => ({ value: m.stackUserId, label: mitgliedName(members, m.stackUserId) ?? "" })),
              ]}
            />
          </Field>
          {/* Design-System-Migration P2: bewusst NICHT auf `Input` migriert — `Input` rendert
              ausschliesslich ein `<input>`, waehrend hier ein `<textarea>` steht (Muster
              `catalog.tsx`/`settings.tsx`: Textbereiche bleiben roh). */}
          <Field label="Notizen"><Textarea name="notes" className="min-h-[70px]" /></Field>
          <div className="flex justify-end gap-3 pt-1">
            <Button type="button" variant="secondary" onClick={() => setCreateOpen(false)}>Abbrechen</Button>
            <Button type="submit" disabled={busy}>{busy ? "Speichert …" : "Aufgabe anlegen"}</Button>
          </div>
        </form>
      </Drawer>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  // min-w-0: `Field` ist in mehreren Grids (Prioritaet/Faellig-am unten) direktes Grid-Item —
  // s. VeranstaltungsDatenDrawer.tsx fuer die Herleitung. In Block-Kontext wirkungslos, darum
  // hier zentral statt an jedem Aufrufer.
  return (
    <label className="block min-w-0">
      <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{label}</span>
      {children}
    </label>
  );
}
